import API_BASE_URL from "./API_BASE_URL.ts";
import apiClient from "./index.ts";
import { invalidateSessionCache } from "./_cache";

/**
 * ### Fetch Visa Questions
 * Fetches the questions for a given visa type
 * @param visaType - The type of visa to fetch questions for
 * @returns The questions for the given visa type in an array
 */
export const fetchVisaQuestions = async (
  visaType: VisaType
): Promise<EligibilityFormQuestion[]> => {
  const response = await apiClient.get<EligibilityFormQuestion[]>(
    `${API_BASE_URL}/eligibility-questions/${visaType}`
  );
  return response.data;
};

/**
 * The type of visa to fetch questions for
 */
export enum VisaType {
  N_400 = "N-400",
  I_90 = "I-90",
  I_751 = "I-751",
  I_131 = "I-131",
  I_130 = "I-130",
}

// const applicationTypes = [
//   { label: "N-400 - Application for Naturalization", link: "/visa-application/N-400" },
//   { label: "I-130 - Petition for Alien Relative", link: "/visa-application/I-130" },
//   { label: "I-90 - Green Card Renewal/Replacement", link: "/visa-application/f1" },
//   { label: "I-751 - Green Card Conditions Removal ", link: "/visa-application/b2" },
//   { label: "I-131 - Application for Travel Documents, Parole Documents, and Arrival/Departure Records ", link: "/visa-application/b2" },
// ];

export enum VisaTypeLabels {
  N_400 = "N-400 - Application for Naturalization",
  I_90 = "I-90 - Green Card Renewal/Replacement",
  I_751 = "I-751 - Green Card Conditions Removal ",
  I_131 = "I-131 - Application for Travel Documents, Parole Documents, and Arrival/Departure Records ",
  I_130 = "I-130 - Petition for Alien Relative",
}

export interface EligibilityFormQuestion {
  id: number;
  visaType: VisaType;
  questionText: string;
  hint: string;
  eligibilityNote: string;
  contextLine1: string;
  contextLine2: string;
  suggestedTip: string;
  isTerminal: boolean;
  order: number;
  warning: string;
  multiSelect: boolean;
  contributesToEligibility: boolean;
  /** Incorrect answer produces warn-tier (still eligible) instead of hard fail */
  incorrectAsWarning?: boolean;
  lastQuestion: boolean;
  options?: EligibilityFormOption[];
}

export interface EligibilityFormOption {
  id: number;
  key: string;
  text: string;
  explanation: string;
  isCorrect: boolean;
  questionId: number;
  tips: string;
  priority: number;
  contributesToEligibility: boolean;
  nextQuestions: Omit<EligibilityFormQuestion, "options">[];
}

/**
 * ### Create Application
 * Creates a new application with beneficiary name and visa type
 * Called AFTER user is logged in and wants to proceed
 * @param beneficiaryName - Full name of the beneficiary
 * @param visaType - The type of visa application
 * @returns The created application details
 */
export const createApplication = async (
  beneficiaryName: string,
  visaType: VisaType,
  options?: {
    idempotencyKey?: string | null;
  }
): Promise<{
  id: number;
  userId: number;
  beneficiaryName: string;
  visaType: string;
  stage: string;
  isQualified: boolean | null;
  createdAt: string;
}> => {
  // Task 149: no token is read here. The session cookie authenticates this,
  // and the API client adds the CSRF token to writes.
  const response = await apiClient.post(
    `${API_BASE_URL}/applications`,
    {
      beneficiaryName,
      visaType,
    },
    {
      headers: {
        ...(options?.idempotencyKey
          ? { "X-Idempotency-Key": options.idempotencyKey }
          : {}),
      },
    }
  );
  return response.data.data;
};

/**
 * ### Submit Eligibility Responses
 * Submits eligibility responses to an existing application
 * Called after application is created
 * @param applicationId - The ID of the application
 * @param responses - Array of eligibility responses
 * @returns The submission result including qualification status
 */
export const submitEligibilityResponses = async (
  applicationId: number,
  responses: Array<{
    questionId: number;
    optionId: number;
    answerText: string;
    explanation: string | null;
    isReview: boolean;
  }>
): Promise<{
  application: {
    id: number;
    beneficiaryName: string;
    visaType: string;
    stage: string;
    isQualified: boolean;
    qualificationScore: number | null;
  };
  responses: any[];
  isQualified: boolean;
}> => {
  // Task 149: no token is read here. The session cookie authenticates this,
  // and the API client adds the CSRF token to writes.
  const response = await apiClient.post(
    `${API_BASE_URL}/applications/${applicationId}/responses`,
    { responses },
    {
      headers: {
      },
    }
  );
  invalidateSessionCache(`application:${applicationId}`);
  return response.data.data;
};

/**
 * ### Get User Applications
 * Fetches all applications for the logged-in user
 * @returns Array of applications with their details
 */
export const getUserApplications = async (): Promise<
  Array<{
    id: number;
    userId: number;
    beneficiaryName: string;
    visaType: string;
    stage: string;
    isQualified: boolean | null;
    qualificationScore: number | null;
    packageId: number | null;
    createdAt: string;
    updatedAt: string;
  }>
> => {
  // Task 149: no token is read here. The session cookie authenticates this,
  // and the API client adds the CSRF token to writes.
  const response = await apiClient.get(`${API_BASE_URL}/applications`);
  return response.data.data || [];
};
